/* BarTreats.jsx - İkram (guest treat) component, embedded inside each Bar Stock page */
import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Trash2, Save, Wine, Coffee, UtensilsCrossed, Package, Settings, Edit2, Plus, FileText } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { formatTRNumber, parseLocaleNumber } from '../lib/utils';

const CAT_META = {
    alkollu: { label: 'Alkollü', icon: Wine, color: 'bg-rose-100 text-rose-700 border-rose-200' },
    soft: { label: 'Soft', icon: Coffee, color: 'bg-sky-100 text-sky-700 border-sky-200' },
    yemek: { label: 'Yemek', icon: UtensilsCrossed, color: 'bg-amber-100 text-amber-700 border-amber-200' },
    diger: { label: 'Diğer', icon: Package, color: 'bg-slate-100 text-slate-700 border-slate-200' },
};

export default function BarTreats({ barId, date }) {
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin';

    const [products, setProducts] = useState([]);
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);

    // Form state
    const [roomNumber, setRoomNumber] = useState('');
    const [cart, setCart] = useState([]);  // {product_id, product_name, category, quantity, unit_price_eur}
    const [saving, setSaving] = useState(false);

    // Product manager dialog
    const [pmOpen, setPmOpen] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [pmForm, setPmForm] = useState({ name: '', category: 'yemek', unit_price_eur: '' });

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line
    }, [barId, date]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [prodRes, recRes] = await Promise.all([
                api.get('/treats/products'),
                api.get('/treats', { params: { bar_id: barId, date_from: date, date_to: date } }),
            ]);
            setProducts(prodRes.data || []);
            setRecords(recRes.data || []);
        } catch {
            toast.error('İkram verisi yüklenemedi');
        } finally {
            setLoading(false);
        }
    };

    const addProductToCart = (product) => {
        setCart(prev => {
            const existing = prev.find(c => c.product_id === product.id);
            if (existing) {
                return prev.map(c => c === existing ? { ...c, quantity: c.quantity + 1 } : c);
            }
            return [...prev, {
                product_id: product.id,
                product_name: product.name,
                category: product.category,
                quantity: 1,
                unit_price_eur: product.unit_price_eur || 0,
            }];
        });
    };

    const updateCartItem = (idx, field, value) => {
        setCart(prev => prev.map((c, i) => i === idx ? { ...c, [field]: value } : c));
    };

    const removeFromCart = (idx) => {
        setCart(prev => prev.filter((_, i) => i !== idx));
    };

    const cartTotalEur = cart.reduce((s, c) => s + (parseFloat(c.quantity) || 0) * (parseFloat(c.unit_price_eur) || 0), 0);

    const saveRecord = async () => {
        if (!roomNumber.trim()) {
            toast.error('Oda numarası girin');
            return;
        }
        if (cart.length === 0) {
            toast.error('En az bir ürün ekleyin');
            return;
        }
        setSaving(true);
        try {
            await api.post('/treats/batch', {
                date,
                bar_id: barId,
                room_number: roomNumber.trim(),
                items: cart.map(c => ({
                    product_id: c.product_id,
                    product_name: c.product_name,
                    category: c.category,
                    quantity: parseLocaleNumber(c.quantity) || 1,
                    unit_price_eur: parseLocaleNumber(c.unit_price_eur) || 0,
                })),
            });
            toast.success(`${cart.length} ikram kaydı eklendi`);
            setCart([]);
            setRoomNumber('');
            fetchData();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Kaydetme başarısız');
        } finally {
            setSaving(false);
        }
    };

    const deleteRecord = async (id) => {
        if (!window.confirm('Bu ikram kaydını silmek istediğinize emin misiniz?')) return;
        try {
            await api.delete(`/treats/${id}`);
            toast.success('Silindi');
            fetchData();
        } catch {
            toast.error('Silme başarısız');
        }
    };

    // Product manager actions
    const openPmCreate = () => {
        setEditingProduct(null);
        setPmForm({ name: '', category: 'yemek', unit_price_eur: '' });
        setPmOpen(true);
    };

    const openPmEdit = (p) => {
        setEditingProduct(p);
        setPmForm({ name: p.name, category: p.category, unit_price_eur: p.unit_price_eur });
        setPmOpen(true);
    };

    const savePm = async () => {
        if (!pmForm.name.trim()) {
            toast.error('Ürün adı gerekli');
            return;
        }
        try {
            const payload = {
                name: pmForm.name.trim(),
                category: pmForm.category,
                unit_price_eur: parseLocaleNumber(pmForm.unit_price_eur),
            };
            if (editingProduct) {
                await api.put(`/treats/products/${editingProduct.id}`, payload);
                toast.success('Güncellendi');
            } else {
                await api.post('/treats/products', payload);
                toast.success('Eklendi');
            }
            setPmOpen(false);
            fetchData();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Kaydetme başarısız');
        }
    };

    const deletePm = async (id) => {
        if (!window.confirm('Bu ürünü ikram katalogundan silmek istediğinize emin misiniz?')) return;
        try {
            await api.delete(`/treats/products/${id}`);
            toast.success('Silindi');
            fetchData();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Silme başarısız');
        }
    };

    const downloadPdf = () => {
        const params = new URLSearchParams({ date_from: date, date_to: date, bar_id: barId });
        const token = localStorage.getItem('token');
        if (token) params.set('token', token);
        window.open(`${process.env.REACT_APP_BACKEND_URL}/api/treats/export/pdf?${params}`, '_blank');
    };

    const totalEur = records.reduce((s, r) => s + (r.total_eur || 0), 0);

    return (
        <div className="space-y-4" data-testid="bar-treats">
            {/* Quick-add product cards */}
            <Card>
                <CardContent className="p-4 space-y-4">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <div>
                            <div className="text-sm font-semibold text-slate-700">Hızlı İkram Ekle</div>
                            <div className="text-xs text-slate-500">Ürüne tıklayın → sepete eklensin → oda no girip kaydedin</div>
                        </div>
                        {isAdmin && (
                            <Button variant="outline" size="sm" onClick={openPmCreate} data-testid="treat-add-product-btn">
                                <Settings className="w-4 h-4 mr-1" /> Ürün Yönet
                            </Button>
                        )}
                    </div>

                    {loading ? (
                        <div className="text-center text-slate-400 py-4 text-sm">Yükleniyor...</div>
                    ) : products.length === 0 ? (
                        <div className="text-center text-slate-400 py-4 text-sm">Henüz ürün yok</div>
                    ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                            {products.map(p => {
                                const meta = CAT_META[p.category] || CAT_META.diger;
                                const Icon = meta.icon;
                                return (
                                    <div key={p.id} className="relative group">
                                        <button
                                            type="button"
                                            onClick={() => addProductToCart(p)}
                                            className={`w-full ${meta.color} border rounded-xl p-3 text-left hover:scale-[1.02] hover:shadow-md transition-all`}
                                            data-testid={`treat-product-${p.id}`}
                                        >
                                            <div className="flex items-center gap-1.5 mb-1">
                                                <Icon className="w-4 h-4" />
                                                <span className="text-[10px] uppercase opacity-70">{meta.label}</span>
                                            </div>
                                            <div className="font-semibold text-sm truncate">{p.name}</div>
                                            <div className="text-xs opacity-80 mt-1">{formatTRNumber(p.unit_price_eur, 2)} €</div>
                                        </button>
                                        {isAdmin && (
                                            <div className="absolute top-1 right-1 hidden group-hover:flex gap-1">
                                                <button onClick={(e) => { e.stopPropagation(); openPmEdit(p); }} className="bg-white/80 rounded p-1 hover:bg-white" title="Düzenle">
                                                    <Edit2 className="w-3 h-3" />
                                                </button>
                                                <button onClick={(e) => { e.stopPropagation(); deletePm(p.id); }} className="bg-white/80 rounded p-1 hover:bg-white text-red-600" title="Sil">
                                                    <Trash2 className="w-3 h-3" />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Cart + Save */}
            {cart.length > 0 && (
                <Card className="border-2 border-emerald-200 bg-emerald-50/40">
                    <CardContent className="p-4 space-y-3">
                        <div className="flex items-center justify-between flex-wrap gap-3">
                            <div className="flex-1 min-w-[220px]">
                                <Label className="text-xs">Oda No</Label>
                                <Input value={roomNumber} onChange={(e) => setRoomNumber(e.target.value)} placeholder="Örn: 215" className="h-10" data-testid="treat-room-number" />
                            </div>
                            <div className="text-right">
                                <div className="text-xs text-emerald-700">Sepet Toplam</div>
                                <div className="text-xl font-bold text-emerald-800">{formatTRNumber(cartTotalEur, 2)} €</div>
                            </div>
                        </div>
                        <div className="border rounded-lg bg-white divide-y">
                            {cart.map((c, idx) => {
                                const meta = CAT_META[c.category] || CAT_META.diger;
                                const itemTotal = (parseLocaleNumber(c.quantity) || 0) * (parseLocaleNumber(c.unit_price_eur) || 0);
                                return (
                                    <div key={idx} className="flex items-center gap-2 px-3 py-2" data-testid={`treat-cart-${idx}`}>
                                        <Badge variant="outline" className={meta.color}>{meta.label}</Badge>
                                        <span className="flex-1 text-sm font-medium">{c.product_name}</span>
                                        <Input type="text" inputMode="decimal" value={c.quantity} onChange={(e) => updateCartItem(idx, 'quantity', e.target.value)} className="w-16 h-8 text-center" />
                                        <Input type="text" inputMode="decimal" value={c.unit_price_eur} onChange={(e) => updateCartItem(idx, 'unit_price_eur', e.target.value)} className="w-20 h-8 text-right" />
                                        <span className="text-xs text-slate-500">€</span>
                                        <span className="w-20 text-right font-semibold text-sm">{formatTRNumber(itemTotal, 2)} €</span>
                                        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => removeFromCart(idx)}>
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="flex justify-end">
                            <Button onClick={saveRecord} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="treat-save-btn">
                                <Save className="w-4 h-4 mr-2" /> {saving ? 'Kaydediliyor...' : 'Kaydet'}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Today's records */}
            <Card>
                <CardContent className="p-4 space-y-3">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <div>
                            <div className="text-sm font-semibold text-slate-700">{date} · {records.length} kayıt</div>
                            <div className="text-xs text-slate-500">
                                Toplam: <span className="font-semibold text-emerald-700">{formatTRNumber(totalEur, 2)} €</span>
                            </div>
                        </div>
                        <Button variant="outline" size="sm" onClick={downloadPdf} className="border-red-300 text-red-700 hover:bg-red-50">
                            <FileText className="w-4 h-4 mr-1" /> Günlük PDF
                        </Button>
                    </div>

                    <div className="overflow-x-auto border rounded-lg">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-100 border-b">
                                <tr>
                                    <th className="text-left p-2 font-medium text-slate-600">Oda</th>
                                    <th className="text-left p-2 font-medium text-slate-600">Ürünler</th>
                                    <th className="text-right p-2 font-medium text-slate-600">Toplam €</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {records.length === 0 ? (
                                    <tr><td colSpan="4" className="p-4 text-center text-slate-400 text-sm">Bu tarih için ikram kaydı yok</td></tr>
                                ) : (() => {
                                    // Group records by room_number for this day
                                    const groups = {};
                                    for (const r of records) {
                                        const key = r.room_number;
                                        if (!groups[key]) groups[key] = { room: r.room_number, items: [], total_eur: 0, ids: [] };
                                        groups[key].items.push(r);
                                        groups[key].total_eur += r.total_eur || 0;
                                        groups[key].ids.push(r.id);
                                    }
                                    return Object.values(groups).map((g) => (
                                        <tr key={g.room} className="border-b hover:bg-slate-50" data-testid={`treat-group-${g.room}`}>
                                            <td className="p-2 align-top">
                                                <Badge variant="outline" className="text-sm">{g.room}</Badge>
                                            </td>
                                            <td className="p-2 align-top">
                                                <div className="space-y-1">
                                                    {g.items.map(it => {
                                                        const meta = CAT_META[it.category] || CAT_META.diger;
                                                        return (
                                                            <div key={it.id} className="flex items-center gap-2 text-sm">
                                                                <Badge variant="outline" className={`${meta.color} text-[10px]`}>{meta.label}</Badge>
                                                                <span className="flex-1">{it.product_name}</span>
                                                                <span className="text-xs text-slate-500">x{formatTRNumber(it.quantity || 0, 2)}</span>
                                                                <span className="text-xs font-medium text-emerald-700">{formatTRNumber(it.total_eur || 0, 2)} €</span>
                                                                <Button variant="ghost" size="sm" className="h-6 w-6 text-red-500 p-0" onClick={() => deleteRecord(it.id)}>
                                                                    <Trash2 className="w-3 h-3" />
                                                                </Button>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </td>
                                            <td className="p-2 text-right align-top font-bold text-emerald-700">{formatTRNumber(g.total_eur, 2)} €</td>
                                            <td className="p-2"></td>
                                        </tr>
                                    ));
                                })()}
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>

            {/* Product Manager Dialog */}
            <Dialog open={pmOpen} onOpenChange={setPmOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Plus className="w-5 h-5 text-emerald-700" />
                            {editingProduct ? 'İkram Ürünü Düzenle' : 'Yeni İkram Ürünü'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <div>
                            <Label>Ürün Adı *</Label>
                            <Input value={pmForm.name} onChange={(e) => setPmForm({ ...pmForm, name: e.target.value })} placeholder="Örn: Meyve Tabağı" data-testid="treat-pm-name" />
                        </div>
                        <div>
                            <Label>Kategori *</Label>
                            <Select value={pmForm.category} onValueChange={(v) => setPmForm({ ...pmForm, category: v })}>
                                <SelectTrigger data-testid="treat-pm-category"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="alkollu">Alkollü</SelectItem>
                                    <SelectItem value="soft">Soft</SelectItem>
                                    <SelectItem value="yemek">Yemek</SelectItem>
                                    <SelectItem value="diger">Diğer</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Birim Fiyat (€)</Label>
                            <Input type="text" inputMode="decimal" value={pmForm.unit_price_eur} onChange={(e) => setPmForm({ ...pmForm, unit_price_eur: e.target.value })} placeholder="0,00" data-testid="treat-pm-price" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setPmOpen(false)}>İptal</Button>
                        <Button onClick={savePm} className="bg-emerald-600 hover:bg-emerald-700" data-testid="treat-pm-save">{editingProduct ? 'Güncelle' : 'Ekle'}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
